import { z } from 'zod';

const WIDGET_CLIENT_KEY_PATTERN = /^(test|live)_gck_/;

const toWidgetClientKey = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return WIDGET_CLIENT_KEY_PATTERN.test(trimmed) ? trimmed : undefined;
};

/** 다듬은 값이 https 주소일 때만 그대로 쓴다 — 빈 값·형식 오류·다른 스킴(http·javascript 등)은 미설정이다. */
const toHttpsUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  try {
    return new URL(trimmed).protocol === 'https:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * 클라이언트 환경변수 스키마 — 부팅 검증(env.ts)과 스펙이 같이 쓴다.
 *
 * Vite 는 `VITE_` 접두사가 붙은 값만 번들에 노출한다.
 */
export const envSchema = z.object({
  VITE_API_URL: z.url().default('http://localhost:4000'),
  VITE_TRPC_URL: z.url().default('http://localhost:4000/trpc'),
  // 토스페이먼츠 결제위젯 클라이언트 키(test_gck_ · live_gck_). 결제 하나 때문에 앱 전체 부팅을 막지 않도록
  // 선택값이다 — 다듬은 값이 비었거나 형식이 다르면(공백·시크릿 키 test_gsk_·API 개별 연동 키 test_ck_ 등)
  // 미설정으로 보고, 결제 페이지가 설정 오류를 보여 준다.
  VITE_TOSS_CLIENT_KEY: z.preprocess(toWidgetClientKey, z.string().optional()),
  // 고객 주문하기 gate — 정확히 'true' 일 때만 내 견적의 '주문하기'와 주문하기 화면(orders.getPlacement 로 읽음)을
  // 연다. 그 밖의 값·미설정은 닫힘(fail-closed). dev 배포 이미지(apps/web/Dockerfile·deploy-web.yml)는 GitHub dev 변수
  // ORDER_PLACEMENT_ENABLED(api 와 같은 변수)에서 받는다 — 등록하지 않으면 빈 값이라 닫힘이다. api 의 ORDER_PLACEMENT_ENABLED 와
  // 둘 다 'true' 여야 열린다 — 여는 절차는 docs/operations/orders-integration.md "web·admin 연결과 주문하기 gate".
  VITE_ORDER_PLACEMENT_ENABLED: z
    .unknown()
    .optional()
    .transform((value) => value === 'true'),
  // 약관·개인정보처리방침 페이지 — 정확히 'true' 일 때만 /terms·/privacy 에 본문을 보인다. 그 밖의 값·미설정은 준비 중 화면이다.
  // 운영 배포(deploy-prod.yml)는 true 로 빌드한다. 화면은 이 런타임 값이 아니라 빌드 시점 상수(app/legal-draft-loader.ts 의
  // import.meta.env — 옛 이름 VITE_LEGAL_DRAFT_ENABLED 도 한 릴리스 동안 함께 읽는다)로 정한다 — 끈 빌드에 본문이 실리지 않게.
  // 여기 두는 것은 같은 규칙의 검증과, 배포 전달 경로 스펙(Dockerfile·deploy-web.yml 이 모든 키를 넘기는지)이 이 목록을 읽기 때문이다.
  VITE_LEGAL_PAGES_ENABLED: z
    .unknown()
    .optional()
    .transform((value) => value === 'true'),
  // 상품 상세 "스토어에 없는 상품이 필요하신가요?" 안내의 카카오톡 채널 주소(비회원 견적 요청 설계 G20). 선택값이다 —
  // 지금은 운영 설정(siteSettings.getPublic)의 채널 주소가 우선이고 이 값은 그게 비어 있을 때의 대체다(운영 설정 설계 K7, 한 릴리스 뒤 삭제).
  // 다듬은 값이 https 주소가 아니면(미설정·빈 값·형식 오류) 카카오톡 줄만 숨기고 부팅은 막지 않는다. dev 배포 이미지
  // (apps/web/Dockerfile·deploy-web.yml)는 GitHub dev 변수 KAKAO_CHANNEL_URL 에서 받는다 — 빌드에 박히므로 바꾸면 web 을 다시 배포한다.
  VITE_KAKAO_CHANNEL_URL: z.preprocess(toHttpsUrl, z.string().optional()),
});
